<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getInitials, shopMyHubsUrl } from '../../helpers/helper';
import { ROLES, roleLabel, toRole } from '../../helpers/roles';
import { useAuthStore } from '../../stores/auth';
import { useEventStore } from '../../stores/event';
import { useMyHubs } from '../../composables/useMyHubs';
import HubMenu from './HubMenu.vue';
import HubMenuItem from './HubMenuItem.vue';

const authStore = useAuthStore();
const eventStore = useEventStore();
const router = useRouter();
const envURLs = inject('envURLs', {});

const open = ref(false);
const triggerId = 'hub-identity-trigger';

const isBroker = computed(() => toRole(authStore.userRole) === ROLES.BROKER);
const { hubs, load: loadHubs } = useMyHubs();
const offersMyHubs = computed(() => !isBroker.value || hubs.value.length > 1);
onMounted(() => {
  if (isBroker.value) loadHubs();
});

// Read through the store rather than destructuring it. The old sidebar did
// `const { userData } = authStore`, which takes a non-reactive snapshot — the
// reason its avatar initials never appeared for anyone whose profile arrived
// after mount.
const user = computed(() => authStore.userData);
const fullName = computed(() =>
  [user.value?.first_name, user.value?.last_name].filter(Boolean).join(' ')
);
const initials = computed(() => (user.value ? getInitials(user.value) : ''));
const role = computed(() => roleLabel(authStore.userRole));
const hubName = computed(() => eventStore.event.value?.login_event_name || window.store?.subdomain || 'your hub');

const goToProfile = () => {
  open.value = false;
  router.push('/my-profile');
};

// An owner's hubs live in their XtendLive account; a broker's list is a page
// in this hub.
const goToMyHubs = () => {
  open.value = false;
  if (isBroker.value) {
    router.push('/my-hubs');
  } else {
    window.location.href = shopMyHubsUrl(envURLs.eco);
  }
};

const signOut = () => {
  open.value = false;
  router.push('/logout');
};
</script>

<template>
  <div class="hub-identity">
    <span class="hub-identity__text">
      <span class="hub-identity__name">{{ fullName }}</span>
      <span class="hub-identity__sep" aria-hidden="true">·</span>
      <!-- Same roleLabel() the sidebar's context line uses; the casing
           difference between the two is a text-transform, not a second map. -->
      <span class="hub-identity__role">{{ role }}</span>
    </span>

    <button
      :id="triggerId"
      type="button"
      class="hub-identity__avatar"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="`Account menu for ${fullName || 'your account'}`"
      @click="open = !open"
    >
      <img v-if="user?.profile_image" :src="user.profile_image" alt="" class="hub-identity__photo" />
      <span v-else>{{ initials }}</span>
    </button>

    <HubMenu v-model:open="open" :labelled-by="triggerId">
      <div class="hub-identity__who" role="none">
        <b>{{ fullName }}</b>
        <span>{{ role }} · {{ hubName }}</span>
      </div>
      <HubMenuItem label="Profile" @select="goToProfile">
        <template #icon>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="9" cy="8" r="3.2" /><path d="M3.6 20v-1.4A4 4 0 0 1 7.6 14.6h2.8a4 4 0 0 1 4 4V20" /><circle cx="17.4" cy="9" r="2.4" /><path d="M16 14.7h1.8a3 3 0 0 1 3 3V20" />
          </svg>
        </template>
      </HubMenuItem>
      <HubMenuItem v-if="offersMyHubs" label="My Hubs" @select="goToMyHubs">
        <template #icon>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 6.6C10.4 5.2 8.4 4.6 5.4 4.6a1 1 0 0 0-1 1v11.6a1 1 0 0 0 1 1c3 0 5 .6 6.6 2 1.6-1.4 3.6-2 6.6-2a1 1 0 0 0 1-1V5.6a1 1 0 0 0-1-1c-3 0-5 .6-6.6 2z" /><path d="M12 6.6v13.6" />
          </svg>
        </template>
      </HubMenuItem>
      <div class="hub-identity__divider" role="separator" />
      <HubMenuItem label="Sign out" @select="signOut">
        <template #icon>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="5.4" y="3.8" width="9.6" height="16.4" rx="1.4" /><path d="M3.6 20.2h16.8" /><circle cx="12.4" cy="12.2" r=".95" />
          </svg>
        </template>
      </HubMenuItem>
    </HubMenu>
  </div>
</template>

<style scoped>
.hub-identity {
  position: relative; /* HubMenu positions against this */
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.hub-identity__text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font, 'Lato', sans-serif);
  font-size: 14px;
  line-height: 20px;
  color: var(--XL-M-Theme-sys-light-on-surface-variant, #4b444d);
  white-space: nowrap;
}

.hub-identity__role {
  text-transform: lowercase;
}

.hub-identity__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 999px;
  background: #ede1f5;
  color: #5e2f78;
  font-family: var(--font, 'Lato', sans-serif);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.hub-identity__avatar:focus-visible {
  outline: 2px solid #5e2f78;
  outline-offset: 2px;
}

.hub-identity__photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hub-identity__who {
  padding: 9px 11px 10px;
  margin-bottom: 5px;
  border-bottom: 1px solid var(--xl-color-border-subtle);
}

.hub-identity__who b {
  display: block;
  font-size: 13.5px;
  color: var(--xl-color-primary-text);
}

.hub-identity__who span {
  display: block;
  margin-top: 1px;
  font-size: 12px;
  color: var(--xl-color-outline);
}

.hub-identity__divider {
  height: 1px;
  margin: 5px 7px;
  background: var(--xl-color-border-subtle);
}

/* On a phone the name and role go; the avatar stays, so the account — and
   therefore signing out — is reachable at every width. */
@media (max-width: 900px) {
  .hub-identity__text {
    display: none;
  }
}
</style>
